import type { ShopifyProduct } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { Spinner } from '@/components/ui/spinner.js';
import { searchStoreProducts } from './api-stores.js';
import { settingsKeys } from './query-keys.js';

export interface ProductSearchPickerProps {
  storeCode: string;
  triggerLabel?: string;
  onSelect: (product: ShopifyProduct) => void;
}

/**
 * « Rechercher un produit » (`02` §12.2) : recherche par titre via
 * `GET /api/stores/:code/products/search`, partagée par la liste de produits
 * bloquants et le produit « Mot personnalisé » (`store-detail-form.tsx`).
 */
export function ProductSearchPicker({
  storeCode,
  triggerLabel = 'Rechercher un produit',
  onSelect,
}: ProductSearchPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(term.trim());
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [term]);

  const query = useQuery({
    queryKey: settingsKeys.productSearch(storeCode, debounced),
    queryFn: () => searchStoreProducts(storeCode, debounced),
    enabled: open && debounced.length >= 2,
  });

  function reset(): void {
    setTerm('');
    setDebounced('');
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Search aria-hidden="true" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="flex flex-col gap-2">
          <Input
            autoFocus
            placeholder="Titre du produit…"
            aria-label="Titre du produit"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
            }}
          />
          {query.isFetching ? <Spinner label="Recherche…" /> : null}
          {!query.isFetching && debounced.length >= 2 && (query.data?.items.length ?? 0) === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">Aucun produit trouvé.</p>
          ) : null}
          <ul className="flex max-h-64 flex-col gap-0.5 overflow-auto">
            {(query.data?.items ?? []).map((item) => (
              <li key={item.productId}>
                <button
                  type="button"
                  className="flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                    reset();
                  }}
                >
                  <span className="font-medium">{item.title}</span>
                  <span className="text-xs text-muted-foreground">{item.handle}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
